import countries from '@/data/countries.json'
import { useDarkModeStore } from '@/stores/darkModeStore'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { format } from 'date-fns-tz'

export const TIMELINE_TYPES = {
  NOTE: 'notes',
  ENQUIRY: 'enquiry',
  QUOTE: 'quote',
  ORDER: 'order',
}

const flagModules = import.meta.glob('@/assets/images/flags/*.png', {
  eager: true,
  import: 'default',
})

const flagMap = Object.fromEntries(
  Object.entries(flagModules).map(([path, mod]) => {
    const code = path.split('/').pop()?.split('.')[0]
    return [code, mod]
  })
)

export function getFlagUrl(country) {
  const code = country?.length > 2 ? getCountryCode(country) : country
  return flagMap[code.toLowerCase()] ?? flagMap['unknown']
}

export function getFlagUrlByCode(code) {
  return flagMap[code.toLowerCase()] ?? flagMap['unknown']
}

function isDarkModeEnabled() {
  const darkModeStore = useDarkModeStore()
  return darkModeStore?.isDark || document.documentElement.classList.contains('dark')
}

export function getInitials(name) {
  const n = typeof name === 'string' ? name.trim() : ''
  if (!n) return ''
  const parts = n.split(/\s+/)
  const firstInitial = parts[0]?.charAt(0)?.toUpperCase?.() || ''
  const lastInitial = parts[1]?.charAt(0)?.toUpperCase?.() || ''
  return firstInitial + lastInitial
}

export function getProfilePicture(path) {
  if (!path) return null

  const baseUrl = import.meta.env.VITE_APP_ASSETS_URL.replace(/\/+$/, '')
  const trimmedPath = path.replace(/^\/+/, '')

  return `${baseUrl}/${trimmedPath}`
}

export async function copyToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    // Modern browsers with Clipboard API in a secure context
    await navigator.clipboard.writeText(text)
  } else {
    // Fallback for older browsers and non-secure contexts
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.opacity = '0'
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }
}

export function formatFileSize(bytes) {
  if (bytes === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const size = bytes / Math.pow(1024, i)

  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(size)} ${units[i]}`
}

export function getRandomBackground(label, imagePath) {
  if (imagePath) return 'transparent'
  if (!label) return isDarkModeEnabled() ? '#2D2D2D' : '#E5E7EB' // Default background if no label

  const lightColors = [
    '#FFB3BA', // Light Red
    '#FFDFBA', // Peach
    '#FFFFBA', // Light Yellow
    '#BAFFC9', // Light Green
    '#BAE1FF', // Light Blue
    '#D9BAFF', // Lavender
    '#FFC4E1', // Soft Pink
    '#C4E7FF', // Soft Sky Blue
    '#E2F0CB', // Mint Green
    '#FDE2E4', // Blush Pink
    '#E0BBE4', // Light Purple
    '#FFCCF9', // Soft Violet
    '#D5AAFF', // Lilac
    '#B5EAD7', // Aqua
    '#A2D2FF', // Baby Blue
  ]

  const darkColors = [
    '#8B0000', // Dark Red
    '#8B4513', // Saddle Brown
    '#B8860B', // Dark Yellow
    '#2F4F4F', // Dark Slate Gray
    '#1E3A5F', // Deep Navy
    '#4B0082', // Indigo
    '#800080', // Purple
    '#003366', // Dark Blue
    '#556B2F', // Dark Olive Green
    '#8B0000', // Dark Maroon
    '#9932CC', // Dark Orchid
    '#483D8B', // Dark Slate Blue
    '#9400D3', // Dark Violet
    '#008080', // Teal
    '#4682B4', // Steel Blue
  ]

  // Normalize label (lowercase & trimmed)
  label = label.toLowerCase().trim()

  // Efficient hash function using bitwise shift
  const hash = Array.from(label).reduce((sum, char) => (sum << 5) - sum + char.charCodeAt(0), 0)

  const colors = isDarkModeEnabled() ? darkColors : lightColors

  return colors[Math.abs(hash) % colors.length]
}

export function getCountryCode(countryName) {
  if (countryName.length < 3) return countryName
  const countryMap = new Map(countries.map(({ name, code }) => [name, code]))
  return countryMap?.get(countryName)?.toLowerCase() || 'unknown'
}

export function formatDate(dateString, locale = 'en-US', options = {}) {
  if (!dateString) return ''

  const date = new Date(dateString)

  const defaultOptions = { year: 'numeric', month: 'short', day: 'numeric' }
  return new Intl.DateTimeFormat(locale, { ...defaultOptions, ...options }).format(date)
}

export function formatDateTime(dateString) {
  if (!dateString) return ''
  return format(new Date(dateString), 'dd.MM.yyyy HH:mm')
}

export function formatYearMonth(d) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

export function toIsoDate(d) {
  if (!d) return null
  if (d instanceof Date && !isNaN(d)) {
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  }
  return d
}

export function formatPhoneNumber(rawPhone) {
  if (!rawPhone) return ''
  const phoneNumber = parsePhoneNumberFromString(rawPhone)
  return phoneNumber ? phoneNumber.formatInternational() : rawPhone
}

export function formatFloatNumber(number) {
  if (!number && number !== 0) return ''

  return new Intl.NumberFormat('uk-UA', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number)
}

export function formatIntNumber(number) {
  if (!number && number !== 0) return ''

  return new Intl.NumberFormat('uk-UA', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(number)
}

export const getTimelineTypeColor = (type) => {
  switch (type) {
    case 'enquiry':
      return 'bg-blue-500 text-blue-800'
    case 'quote':
      return 'bg-amber-500 text-yellow-800'
    case 'order':
      return 'bg-green-500 text-green-100 dark:text-green-800'
    case 'shipment':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
    case 'download invoice':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300'
    case 'download quote':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300'
    case 'download order':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300'
    case 'download pro forma':
      return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300'
    case 'download credit note':
      return 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-300'
    case 'notes':
      return 'bg-sky-400 text-white dark:text-black'
    case 'ringover':
      return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300'
    case 'ringcentral':
      return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
  }
}

export const capitalize = (str) => {
  const s = typeof str === 'string' ? str.trim() : ''
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

export function formatNotificationDate(isoString) {
  const date = new Date(isoString)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()

  // Fixed en-GB locale: 24-hour time and day-month-year order, regardless of the browser's locale.
  return isToday
    ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
